import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  MenuItem,
  Select,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Divider,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  ExpandMore,
  Edit,
  Delete,
  Add,
  Save,
  Security,
  Info,
  Warning,
  CheckCircle,
  Error,
  Translate as TranslateIcon,
} from '@mui/icons-material';
import { privacyPolicyService, globalService } from '../../services/firebase';
import { PrivacyPolicyData, PolicySection, ImportantNote, TranslatableField } from '../../types';
import { useTranslation } from 'react-i18next';
import { getTranslatedData } from '../../utils/getTranslatedData';
import { produce } from 'immer';

const getTranslatableString = (field: TranslatableField | string | undefined, lang: string): string => {
    if (!field) return '';
    if (typeof field === 'string') return lang === 'tr' ? field : '';
    return field[lang] || '';
};

const ManagePrivacyPolicyPage: React.FC = () => {
  const [policyData, setPolicyData] = useState<PrivacyPolicyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedLang, setSelectedLang] = useState('tr');
  const { t } = useTranslation();

  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [currentSection, setCurrentSection] = useState<PolicySection | null>(null);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [currentNote, setCurrentNote] = useState<ImportantNote | null>(null);

  const iconOptions = ['DataUsage', 'Visibility', 'Lock', 'Share', 'Delete', 'Cookie', 'Security', 'Shield'];

  const fetchPolicyData = useCallback(async () => {
    setLoading(true);
    try {
      let data = await privacyPolicyService.getPrivacyPolicyData();
      if (!data) {
        await privacyPolicyService.initializePrivacyPolicyData();
        data = await privacyPolicyService.getPrivacyPolicyData();
      }
      setPolicyData(data);
    } catch (err: any) {
      setError(t('privacy_policy_admin.errors.load', { message: err.message }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchPolicyData();
  }, [fetchPolicyData]);

  const handleTranslatableFieldChange = (path: (string | number)[], value: string) => {
    setPolicyData(produce(draft => {
        if (!draft) return;
        let current: any = draft;
        for (let i = 0; i < path.length - 1; i++) {
          current = current[path[i]];
        }
        const fieldName = path[path.length - 1];
        let translatableField = current[fieldName];

        if (typeof translatableField !== 'object' || translatableField === null) {
            translatableField = { tr: '', en: '', ar: '' };
        }
        translatableField[selectedLang] = value;
        current[fieldName] = translatableField;
    }));
  };
  
  const handleSimpleFieldChange = (path: (string | number)[], value: any) => {
      setPolicyData(produce(draft => {
          if (!draft) return;
          let current: any = draft;
          for (let i = 0; i < path.length - 1; i++) {
              current = current[path[i]];
          }
          current[path[path.length - 1]] = value;
      }));
  };

  const handleSaveAll = async () => {
    if (!policyData) return;
    setIsSaving(true);
    setError(null);
    try {
      await privacyPolicyService.updatePrivacyPolicyData(policyData);
      setSuccess(t('privacy_policy_admin.success.save'));
       setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(t('privacy_policy_admin.errors.save', { message: err.message }));
    } finally {
      setIsSaving(false);
    }
  };
  
  const createTranslationJobs = (items: any[], textExtractor: (item: any, lang: string) => string | undefined, pathBuilder: (item: any) => (string | number)[]) => {
      const jobs: { text: string; path: (string | number)[] }[] = [];
      items.forEach(item => {
          const text = textExtractor(item, 'tr');
          if (text) {
              jobs.push({ text, path: pathBuilder(item) });
          }
      });
      return jobs;
  };

  const executeTranslations = async (jobs: { text: string; path: (string | number)[] }[]) => {
      const promises = jobs.map(job =>
          globalService.translateTexts({ text: job.text, sourceLang: 'tr', targetLangs: ['en', 'ar'] })
              .then(translations => ({ path: job.path, translations, tr: job.text }))
      );
      return Promise.all(promises);
  };
  
  const applyTranslations = (results: { path: (string | number)[], translations: { [key: string]: string }, tr: string }[]) => {
      setPolicyData(produce(draft => {
          if (!draft) return;
          results.forEach(result => {
              let current: any = draft;
              for (let i = 0; i < result.path.length - 1; i++) {
                  current = current[result.path[i]];
              }
              const fieldName = result.path[result.path.length - 1];
              current[fieldName] = { tr: result.tr, en: result.translations.en, ar: result.translations.ar };
          });
      }));
  };
  
  const handleTranslate = async (translationJobs: { text: string; path: (string | number)[] }[]) => {
      setIsTranslating(true);
      setError(null);
      try {
          const results = await executeTranslations(translationJobs);
          applyTranslations(results);
          setSuccess(t('translationSuccess'));
      } catch (err: any) {
          setError(t('translationError', { message: err.message }));
      } finally {
          setIsTranslating(false);
      }
  };
  
  const handleTranslateMain = () => {
      if (!policyData) return;
      const jobs = [
        ...createTranslationJobs([policyData], item => getTranslatedData(item.title, 'tr'), () => ['title']),
        ...createTranslationJobs([policyData], item => getTranslatedData(item.description, 'tr'), () => ['description'])
      ];
      handleTranslate(jobs);
  };
  
  const handleTranslateAllSections = () => {
      if (!policyData) return;
      const jobs = [
        ...createTranslationJobs(policyData.policySections, item => getTranslatedData(item.title, 'tr'), item => ['policySections', policyData.policySections.findIndex(s => s.id === item.id), 'title']),
        ...policyData.policySections.flatMap((section, sectionIndex) => 
            createTranslationJobs(section.items.map((item, itemIndex) => ({item, itemIndex})), 
            obj => getTranslatedData(obj.item, 'tr'), 
            obj => ['policySections', sectionIndex, 'items', obj.itemIndex])
        )
      ];
      handleTranslate(jobs);
  };

  const handleTranslateAllNotes = () => {
    if (!policyData) return;
      const jobs = [
        ...createTranslationJobs(policyData.importantNotes, item => getTranslatedData(item.title, 'tr'), item => ['importantNotes', policyData.importantNotes.findIndex(s => s.id === item.id), 'title']),
        ...createTranslationJobs(policyData.importantNotes, item => getTranslatedData(item.content, 'tr'), item => ['importantNotes', policyData.importantNotes.findIndex(s => s.id === item.id), 'content'])
      ];
      handleTranslate(jobs);
  };

  const handleOpenSectionDialog = (section?: PolicySection) => {
    const newSection: PolicySection = section 
      ? JSON.parse(JSON.stringify(section))
      : { id: `new-${Date.now()}`, title: {tr: '', en: '', ar: ''}, icon: 'DataUsage', items: [], order: (policyData?.policySections.length || 0) + 1, isActive: true };
    setCurrentSection(newSection);
    setSectionDialogOpen(true);
    setSelectedLang('tr');
  };

  const handleCloseSectionDialog = () => {
    setSectionDialogOpen(false);
    setCurrentSection(null);
  };

  const handleCurrentSectionChange = (field: keyof Omit<PolicySection, 'title' | 'items'>, value: any) => {
    if (!currentSection) return;
    setCurrentSection(produce(draft => {
      if(draft) (draft as any)[field] = value;
    }));
  };
  
  const handleCurrentSectionTranslatableChange = (field: 'title' | 'items', value: string, index?: number) => {
    if (!currentSection) return;
    setCurrentSection(produce(draft => {
        if (!draft) return;
        if(field === 'title') {
          if (typeof draft.title !== 'object' || draft.title === null) draft.title = {tr:'', en:'', ar:''};
          (draft.title as TranslatableField)[selectedLang] = value;
        } else if (field === 'items' && typeof index === 'number') {
            if (typeof draft.items[index] !== 'object' || draft.items[index] === null) draft.items[index] = {tr:'', en:'', ar:''};
            (draft.items[index] as TranslatableField)[selectedLang] = value;
        }
    }));
  };

  const handleTranslateCurrentSection = () => {
    if(!currentSection) return;
    const titleTr = getTranslatedData(currentSection.title, 'tr');
    const itemsTr = currentSection.items.map(item => getTranslatedData(item, 'tr'));

    setIsTranslating(true);
    Promise.all([
      titleTr ? globalService.translateTexts({text: titleTr, sourceLang: 'tr', targetLangs:['en', 'ar']}) : Promise.resolve(null),
      ...itemsTr.map(item => item ? globalService.translateTexts({text: item, sourceLang: 'tr', targetLangs:['en', 'ar']}) : Promise.resolve(null))
    ]).then(([titleTranslations, ...itemsTranslations]) => {
      setCurrentSection(produce(draft => {
        if(!draft) return;
        if(titleTranslations) {
          draft.title = { tr: titleTr, en: titleTranslations.en, ar: titleTranslations.ar };
        }
        draft.items.forEach((_, index) => {
          const itemTr = itemsTr[index];
          const itemTranslations = itemsTranslations[index];
          if(itemTr && itemTranslations) {
            draft.items[index] = { tr: itemTr, en: itemTranslations.en, ar: itemTranslations.ar };
          }
        })
      }))
    }).catch(err => setError(t('translationError')))
    .finally(() => setIsTranslating(false));
  }

  const addSectionItem = () => {
    if (!currentSection) return;
    setCurrentSection(produce(draft => {
        if (draft) draft.items.push({ tr: '', en: '', ar: '' });
    }));
  };

  const removeSectionItem = (index: number) => {
    if (!currentSection) return;
    setCurrentSection(produce(draft => {
      if(draft) draft.items.splice(index, 1);
    }));
  };

  const handleSaveSection = () => {
    if (!policyData || !currentSection || !getTranslatedData(currentSection.title, 'tr').trim()) {
        setError(t('privacy_policy_admin.errors.sectionTitleRequired'));
        return;
    }
    setPolicyData(produce(draft => {
        if(!draft) return;
        const index = draft.policySections.findIndex(s => s.id === currentSection.id);
        if (index > -1) draft.policySections[index] = currentSection;
        else draft.policySections.push(currentSection);
        draft.policySections.sort((a,b) => a.order - b.order);
    }));
    handleCloseSectionDialog();
  };

  const handleDeleteSection = (sectionId: string) => {
    if (!window.confirm(t('privacy_policy_admin.confirmations.deleteSection'))) return;
    setPolicyData(produce(draft => {
        if(draft) {
            draft.policySections = draft.policySections.filter((s: PolicySection) => s.id !== sectionId);
        }
    }));
  };

  const handleOpenNoteDialog = (note?: ImportantNote) => {
    const newNote: ImportantNote = note
      ? JSON.parse(JSON.stringify(note))
      : { id: `new-${Date.now()}`, title: {tr: '', en: '', ar: ''}, content: {tr: '', en: '', ar: ''}, type: 'info', order: (policyData?.importantNotes.length || 0) + 1, isActive: true };
    setCurrentNote(newNote);
    setNoteDialogOpen(true);
    setSelectedLang('tr');
  };

  const handleCloseNoteDialog = () => {
    setNoteDialogOpen(false);
    setCurrentNote(null);
  };
  
  const handleCurrentNoteChange = (field: keyof Omit<ImportantNote, 'title'|'content'>, value: any) => {
    if(!currentNote) return;
    setCurrentNote(produce(draft => {
        if(draft) (draft as any)[field] = value;
    }));
  };

  const handleCurrentNoteTranslatableChange = (field: 'title' | 'content', value: string) => {
    if (!currentNote) return;
    setCurrentNote(produce(draft => {
        if(!draft) return;
        if (typeof (draft as any)[field] !== 'object' || (draft as any)[field] === null) {
          (draft as any)[field] = {tr:'', en:'', ar:''};
        }
        ((draft as any)[field] as TranslatableField)[selectedLang] = value;
    }));
  };

  const handleTranslateCurrentNote = () => {
    if(!currentNote) return;
    const titleTr = getTranslatedData(currentNote.title, 'tr');
    const contentTr = getTranslatedData(currentNote.content, 'tr');

    setIsTranslating(true);
    Promise.all([
      titleTr ? globalService.translateTexts({text: titleTr, sourceLang: 'tr', targetLangs:['en', 'ar']}) : Promise.resolve(null),
      contentTr ? globalService.translateTexts({text: contentTr, sourceLang: 'tr', targetLangs:['en', 'ar']}) : Promise.resolve(null)
    ]).then(([titleTranslations, contentTranslations]) => {
      setCurrentNote(produce(draft => {
        if(!draft) return;
        if(titleTr && titleTranslations) {
          draft.title = { tr: titleTr, en: titleTranslations.en, ar: titleTranslations.ar };
        }
        if(contentTr && contentTranslations) {
          draft.content = { tr: contentTr, en: contentTranslations.en, ar: contentTranslations.ar };
        }
      }))
    }).catch(err => setError(t('translationError')))
    .finally(() => setIsTranslating(false));
  }

  const handleSaveNote = () => {
    if (!policyData || !currentNote || !getTranslatedData(currentNote.title, 'tr').trim()) {
        setError(t('privacy_policy_admin.errors.noteTitleRequired'));
        return;
    }
    setPolicyData(produce(draft => {
        if(!draft) return;
        const index = draft.importantNotes.findIndex(n => n.id === currentNote.id);
        if (index > -1) draft.importantNotes[index] = currentNote;
        else draft.importantNotes.push(currentNote);
        draft.importantNotes.sort((a,b) => a.order - b.order);
    }));
    handleCloseNoteDialog();
  };

  const handleDeleteNote = (noteId: string) => {
    if (!window.confirm(t('privacy_policy_admin.confirmations.deleteNote'))) return;
    setPolicyData(produce(draft => {
        if(draft) {
            draft.importantNotes = draft.importantNotes.filter((n: ImportantNote) => n.id !== noteId);
        }
    }));
  };

  const renderNoteIcon = (type: 'info' | 'warning' | 'success' | 'error') => {
    switch(type) {
      case 'info': return <Info color="info" />;
      case 'warning': return <Warning color="warning" />;
      case 'success': return <CheckCircle color="success" />;
      case 'error': return <Error color="error" />;
      default: return <Info />;
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /></Box>;
  if (!policyData) return <Alert severity="error">{t('privacy_policy_admin.errors.noData')}</Alert>;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
        <Box>
            <Typography variant="h4" component="h1" gutterBottom><Security sx={{ mr: 1, verticalAlign: 'middle' }} />{t('privacy_policy_admin.title')}</Typography>
            <Typography variant="body1" color="text.secondary">{t('privacy_policy_admin.description')}</Typography>
        </Box>
        <Button onClick={handleSaveAll} startIcon={<Save />} variant="contained" disabled={isSaving}>
            {isSaving ? <CircularProgress size={24} color="inherit" /> : t('saveAllChanges')}
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h5" component="h2">{t('privacy_policy_admin.mainContent')}</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
                <ToggleButtonGroup value={selectedLang} exclusive onChange={(_, lang) => lang && setSelectedLang(lang)} size="small">
                    <ToggleButton value="tr">TR</ToggleButton>
                    <ToggleButton value="en">EN</ToggleButton>
                    <ToggleButton value="ar">AR</ToggleButton>
                </ToggleButtonGroup>
                <Tooltip title={t('translateAll')}>
                    <span>
                    <IconButton onClick={handleTranslateMain} disabled={isTranslating || selectedLang !== 'tr'}>
                        {isTranslating ? <CircularProgress size={24}/> : <TranslateIcon />}
                    </IconButton>
                    </span>
                </Tooltip>
            </Stack>
        </Stack>
        <Stack spacing={2}>
            <TextField label={t('privacy_policy_admin.pageTitle')} fullWidth value={getTranslatableString(policyData.title, selectedLang)} onChange={(e) => handleTranslatableFieldChange(['title'], e.target.value)} />
            <TextField label={t('privacy_policy_admin.pageDescription')} fullWidth multiline rows={3} value={getTranslatableString(policyData.description, selectedLang)} onChange={(e) => handleTranslatableFieldChange(['description'], e.target.value)} />
            <FormControlLabel control={<Switch checked={policyData.kvkkCompliant} onChange={(e) => handleSimpleFieldChange(['kvkkCompliant'], e.target.checked)} />} label={t('privacy_policy_admin.kvkkCompliant')} />
        </Stack>
      </Paper>
      
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h5" component="h2">{t('privacy_policy_admin.policySections')}</Typography>
            <Stack direction="row" spacing={1}>
                <Tooltip title={t('translateAll')}>
                  <span>
                    <IconButton onClick={handleTranslateAllSections} disabled={isTranslating || selectedLang !== 'tr'}>
                      {isTranslating ? <CircularProgress size={24}/> : <TranslateIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
                <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenSectionDialog()}>
                    {t('privacy_policy_admin.addSection')}
                </Button>
            </Stack>
        </Stack>
        {policyData.policySections.sort((a,b) => a.order - b.order).map(section => (
          <Accordion key={section.id}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Chip label={`#${section.order}`} size="small" sx={{ mr: 2 }} />
              <Typography sx={{ flexShrink: 0, fontWeight: 500 }}>{getTranslatedData(section.title, selectedLang) || t('untitled')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
                  {section.items.map(item => `• ${getTranslatedData(item, selectedLang)}`).join('\n')}
                </Typography>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <FormControlLabel control={<Switch checked={section.isActive} onChange={(e) => handleSimpleFieldChange(['policySections', policyData.policySections.findIndex(s => s.id === section.id), 'isActive'], e.target.checked)} />} label={t('active')} />
                    <Box>
                        <IconButton onClick={() => handleOpenSectionDialog(section)}><Edit /></IconButton>
                        <IconButton onClick={() => handleDeleteSection(section.id)}><Delete /></IconButton>
                    </Box>
                </Stack>
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>

      <Paper elevation={2} sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h5" component="h2">{t('privacy_policy_admin.importantNotes')}</Typography>
             <Stack direction="row" spacing={1}>
                <Tooltip title={t('translateAll')}>
                  <span>
                    <IconButton onClick={handleTranslateAllNotes} disabled={isTranslating || selectedLang !== 'tr'}>
                      {isTranslating ? <CircularProgress size={24}/> : <TranslateIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
                <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenNoteDialog()}>
                    {t('privacy_policy_admin.addNote')}
                </Button>
            </Stack>
        </Stack>
        {policyData.importantNotes.sort((a,b) => a.order - b.order).map(note => (
            <Accordion key={note.id}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Chip label={`#${note.order}`} size="small" sx={{ mr: 2 }} />
                    <Chip label={note.type} size="small" color={note.type === 'info' ? 'info' : note.type === 'warning' ? 'warning' : note.type === 'success' ? 'success' : 'error'} icon={renderNoteIcon(note.type)} sx={{ mr: 2 }} />
                    <Typography sx={{ flexShrink: 0, fontWeight: 500 }}>{getTranslatedData(note.title, selectedLang) || t('untitled')}</Typography>
                </AccordionSummary>
                 <AccordionDetails>
                  <Stack spacing={2}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
                      {getTranslatedData(note.content, selectedLang)}
                    </Typography>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <FormControlLabel control={<Switch checked={note.isActive} onChange={(e) => handleSimpleFieldChange(['importantNotes', policyData.importantNotes.findIndex(n => n.id === note.id), 'isActive'], e.target.checked)} />} label={t('active')} />
                        <Box>
                            <IconButton onClick={() => handleOpenNoteDialog(note)}><Edit /></IconButton>
                            <IconButton onClick={() => handleDeleteNote(note.id)}><Delete /></IconButton>
                        </Box>
                    </Stack>
                  </Stack>
                </AccordionDetails>
            </Accordion>
        ))}
      </Paper>
      
      {/* Section Dialog */}
      <Dialog open={sectionDialogOpen} onClose={handleCloseSectionDialog} fullWidth maxWidth="md">
        <DialogTitle>{currentSection?.id.startsWith('new-') ? t('privacy_policy_admin.dialog.addSectionTitle') : t('privacy_policy_admin.dialog.editSectionTitle')}</DialogTitle>
        <DialogContent>
          {currentSection && <Stack spacing={2} sx={{ pt: 1 }}>
            <Tabs value={selectedLang} onChange={(_, lang) => setSelectedLang(lang)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tab label="Türkçe" value="tr" />
                <Tab label="English" value="en" />
                <Tab label="العربية" value="ar" />
            </Tabs>
            <TextField label={t('title')} fullWidth value={getTranslatedData(currentSection.title, selectedLang)} onChange={e => handleCurrentSectionTranslatableChange('title', e.target.value)} />
            <Divider>Items</Divider>
            {currentSection.items.map((item, index) => (
                <Stack direction="row" spacing={1} key={index}>
                    <TextField label={`Item ${index + 1}`} fullWidth value={getTranslatedData(item, selectedLang)} onChange={e => handleCurrentSectionTranslatableChange('items', e.target.value, index)} />
                    <IconButton onClick={() => removeSectionItem(index)}><Delete /></IconButton>
                </Stack>
            ))}
            <Button onClick={addSectionItem} startIcon={<Add />}>{t('privacy_policy_admin.dialog.addItem')}</Button>
            <Divider />
            <Stack direction="row" spacing={2} alignItems="center">
                <FormControl fullWidth>
                <InputLabel>Icon</InputLabel>
                <Select value={currentSection.icon} onChange={e => handleCurrentSectionChange('icon', e.target.value)} label="Icon">
                    {iconOptions.map(icon => <MenuItem key={icon} value={icon}>{icon}</MenuItem>)}
                </Select>
                </FormControl>
                <TextField label={t('order')} type="number" value={currentSection.order} onChange={e => handleCurrentSectionChange('order', Number(e.target.value))} />
                <FormControlLabel control={<Switch checked={currentSection.isActive} onChange={e => handleCurrentSectionChange('isActive', e.target.checked)} />} label={t('active')} />
            </Stack>
          </Stack>}
        </DialogContent>
        <DialogActions>
            <Tooltip title={t('translateAll')}><span>
                <IconButton onClick={handleTranslateCurrentSection} disabled={isTranslating || selectedLang !== 'tr'}>
                    {isTranslating ? <CircularProgress size={24} /> : <TranslateIcon />}
                </IconButton>
            </span></Tooltip>
            <Box flexGrow={1} />
            <Button onClick={handleCloseSectionDialog}>{t('cancel')}</Button>
            <Button onClick={handleSaveSection} variant="contained">{t('save')}</Button>
        </DialogActions>
      </Dialog>
      
      {/* Note Dialog */}
      <Dialog open={noteDialogOpen} onClose={handleCloseNoteDialog} fullWidth maxWidth="sm">
        <DialogTitle>{currentNote?.id.startsWith('new-') ? t('privacy_policy_admin.dialog.addNoteTitle') : t('privacy_policy_admin.dialog.editNoteTitle')}</DialogTitle>
        <DialogContent>
            {currentNote && <Stack spacing={2} sx={{ pt: 1 }}>
                <Tabs value={selectedLang} onChange={(_, lang) => setSelectedLang(lang)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tab label="Türkçe" value="tr" />
                    <Tab label="English" value="en" />
                    <Tab label="العربية" value="ar" />
                </Tabs>
                <TextField label={t('title')} fullWidth value={getTranslatedData(currentNote.title, selectedLang)} onChange={e => handleCurrentNoteTranslatableChange('title', e.target.value)} />
                <TextField label={t('content')} fullWidth multiline rows={4} value={getTranslatedData(currentNote.content, selectedLang)} onChange={e => handleCurrentNoteTranslatableChange('content', e.target.value)} />
                <Divider />
                <Stack direction="row" spacing={2} alignItems="center">
                    <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select value={currentNote.type} onChange={e => handleCurrentNoteChange('type', e.target.value)} label="Type">
                        <MenuItem value="info">Info</MenuItem>
                        <MenuItem value="warning">Warning</MenuItem>
                        <MenuItem value="success">Success</MenuItem>
                        <MenuItem value="error">Error</MenuItem>
                    </Select>
                    </FormControl>
                    <TextField label={t('order')} type="number" value={currentNote.order} onChange={e => handleCurrentNoteChange('order', Number(e.target.value))} />
                    <FormControlLabel control={<Switch checked={currentNote.isActive} onChange={e => handleCurrentNoteChange('isActive', e.target.checked)} />} label={t('active')} />
                </Stack>
            </Stack>}
        </DialogContent>
        <DialogActions>
            <Tooltip title={t('translateAll')}><span>
                <IconButton onClick={handleTranslateCurrentNote} disabled={isTranslating || selectedLang !== 'tr'}>
                    {isTranslating ? <CircularProgress size={24} /> : <TranslateIcon />}
                </IconButton>
            </span></Tooltip>
            <Box flexGrow={1} />
            <Button onClick={handleCloseNoteDialog}>{t('cancel')}</Button>
            <Button onClick={handleSaveNote} variant="contained">{t('save')}</Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default ManagePrivacyPolicyPage; 